// Shared QR printing workspace for entry, auction, and future QR destinations.
// Owns six sizes, content options, stable sponsor selection, screen/physical
// sheets, pagination, asset readiness, and browser print cleanup. Callers supply
// cards ({id, car, url}), filtered cards, event assets, and page-specific filters.
// Preserves the CSS/DOM contracts established by admin/qr_print.js and QrCard.
import React, { useEffect, useMemo, useRef, useState } from "react";
import QrCard from "./QrCard";
import { showStatus } from "../utils/status_helpers";

export const QR_PRINT_SIZES = [
  ["8x10-portrait", "8 × 10 — Portrait"],
  ["8x10", "8 × 10 — Landscape"],
  ["4x5-portrait", "4 × 5 — Portrait"],
  ["4x5-landscape", "4 × 5 — Landscape"],
  ["2x3-portrait", "2 × 3 — Portrait"],
  ["2x3-landscape", "2 × 3 — Landscape"],
];
const CONTENT_OPTIONS = [
  ["showQrCode", "QR Code"], ["showLogo", "Logo"],
  ["showCategory", "Category"], ["showEntry", "Entry"],
  ["showOwner", "Owner"], ["showSponsors", "Sponsors"],
  ["showBackground", "Background Image"],
];
const chunk = (items, size) => {
  const sheets = [];
  for (let index = 0; index < items.length; index += size) sheets.push(items.slice(index, index + size));
  return sheets;
};

const selectSponsors = (sponsors, topLevels) => {
  const matched = sponsors.filter((sponsor) => topLevels.includes(sponsor.level));
  const eligible = [...(matched.length ? matched : sponsors)];
  for (let index = eligible.length - 1; index > 0; index -= 1) {
    const other = Math.floor(Math.random() * (index + 1));
    [eligible[index], eligible[other]] = [eligible[other], eligible[index]];
  }
  return eligible.slice(0, 6);
};

// Await only this workspace's images. Broken images must not block printing.
const waitForImages = (root) => Promise.all(Array.from(root?.querySelectorAll("img") || []).map((image) => {
  if (image.complete) return Promise.resolve();
  return new Promise((resolve) => {
    const finish = () => {
      image.removeEventListener("load", finish);
      image.removeEventListener("error", finish);
      clearTimeout(timeout);
      resolve();
    };
    const timeout = setTimeout(finish, 10000);
    image.addEventListener("load", finish, { once: true });
    image.addEventListener("error", finish, { once: true });
    if (image.complete) finish();
  });
}));

export default function QrPrintView({
  cards, filteredCards = cards, single = false, filters = null, onResetFilters,
  title = "🖨️ QR Print View", description = "Print QR codes for registered entries",
  categoriesMap = {}, eventName = "", assets,
}) {
  const [printSize, setPrintSize] = useState("8x10");
  const [cardOptions, setCardOptions] = useState(() => Object.fromEntries(CONTENT_OPTIONS.map(([key]) => [key, true])));
  const [page, setPage] = useState(1);
  const [printAll, setPrintAll] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [notification, setNotification] = useState(null);
  const root = useRef(null);
  const printStyle = useRef(null);
  const mounted = useRef(true);
  const printInFlight = useRef(false);
  const { eventImage, eventLogo, useImageOnQr, sponsors, topLevels } = assets;

  // Select from the entire loaded set: filtering, size changes, and rendering
  // the physical copy must not randomize sponsor logos again.
  const sponsorSelections = useMemo(() => Object.fromEntries(cards.map((card) =>
    [card.id, selectSponsors(sponsors, topLevels)]
  )), [cards, sponsors, topLevels]);
  useEffect(() => setPage(1), [filteredCards]);

  const cleanupPrint = () => {
    document.body.classList.remove("print-all-mode");
    printStyle.current?.remove();
    printStyle.current = null;
  };
  useEffect(() => {
    mounted.current = true;
    window.addEventListener("afterprint", cleanupPrint);
    return () => {
      mounted.current = false;
      window.removeEventListener("afterprint", cleanupPrint);
      cleanupPrint();
    };
  }, []);

  const totalPages = Math.max(1, Math.ceil(filteredCards.length / 12));
  const currentPage = Math.min(page, totalPages);
  const currentCards = single || printAll ? filteredCards : filteredCards.slice((currentPage - 1) * 12, currentPage * 12);
  const compact = printSize.startsWith("2x3");
  const perSheet = compact ? 4 : printSize.startsWith("4x5") ? 2 : 1;
  const goToPage = (number) => {
    setPage(Math.max(1, Math.min(number, totalPages)));
    root.current?.scrollIntoView?.({ behavior: "smooth" });
  };
  const handlePrint = async () => {
    if (printInFlight.current || !currentCards.length) return;
    printInFlight.current = true;
    setPrinting(true);
    try {
      await waitForImages(root.current);
      if (document.fonts?.ready) await document.fonts.ready;
      if (!mounted.current) return;
      if (!single && printAll) document.body.classList.add("print-all-mode");
      const style = document.createElement("style");
      style.id = "lgv-qr-print-page-size";
      style.textContent = printSize === "8x10-portrait"
        ? "@page { size: letter portrait; margin: 0; }"
        : "@page { size: letter landscape; margin: 0; }";
      document.head.appendChild(style);
      printStyle.current = style;
      window.print();
    } catch (error) {
      console.warn("QR print dialog could not open", { message: error.message });
      if (mounted.current) showStatus(setNotification, { type: "error", message: "Unable to open printing. Please try again." });
    } finally {
      cleanupPrint();
      printInFlight.current = false;
      if (mounted.current) setPrinting(false);
    }
  };
  const renderCard = (card) => <QrCard
    car={card.car} voteUrl={card.url} eventLogo={eventLogo}
    eventImage={useImageOnQr && cardOptions.showBackground ? eventImage : null}
    sponsorLogos={sponsorSelections[card.id] || []} categoriesMap={categoriesMap}
    eventName={eventName} cardOptions={cardOptions}
  />;

  return (
    <section id="top-content" ref={root} className={`container-fluid py-5 ${single ? "qr-print-single-wrapper" : "qr-print-all-wrapper"} qr-print-size-${printSize}`}>
      <div className="row mb-4"><div className="col text-start">
        <h1 className="display-5">{title}</h1><p className="lead text-muted">{description}</p>
      </div></div>
      <div className="white-section p-4 rounded shadow-sm bg-white qr-print-content">
        {eventImage && useImageOnQr && cardOptions.showBackground && <div className="qr-print-overlay" />}
        <div className="d-print-none mb-3">
          {notification && <div role="alert" className="alert alert-danger">{notification.message}</div>}
          <fieldset disabled={printing}>
          <div className="row mb-3">
            {!single && filters}
            <div className="col-md-6 mb-2">
              <label className="form-label" htmlFor="qr-print-size">Print Size</label>
              <select id="qr-print-size" className="form-select" value={printSize} onChange={(event) => setPrintSize(event.target.value)}>
                {QR_PRINT_SIZES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
          </div>
          <div className="row mb-3"><div className="col-12">
            <div className="form-label mb-2">Card Content</div>
            <div className="d-flex flex-wrap gap-4">
              {CONTENT_OPTIONS.map(([key, label]) => <div className="form-check" key={key}>
                <input id={key} className="form-check-input" type="checkbox" checked={cardOptions[key]}
                  onChange={() => setCardOptions((previous) => ({ ...previous, [key]: !previous[key] }))} />
                <label className="form-check-label" htmlFor={key}>{label}</label>
              </div>)}
            </div>
          </div></div>
          <div className="row"><div className="col-12 d-flex justify-content-end gap-2">
            {!single && <button type="button" className="btn btn-secondary" onClick={() => { onResetFilters?.(); setPrintAll(false); setPage(1); }}>Reset</button>}
            <button type="button" className="btn btn-primary" disabled={printing || !currentCards.length} onClick={handlePrint}>{printing ? "Preparing..." : "Print"}</button>
            {!single && <button type="button" className={`btn ${printAll ? "btn-warning" : "btn-outline-warning"}`} disabled={printing || !filteredCards.length} onClick={() => setPrintAll((previous) => !previous)}>{printAll ? "Paginated View" : "Print All"}</button>}
          </div></div>
          </fieldset>
        </div>
        {!currentCards.length && <div className="alert alert-info d-print-none">No QR codes match the current filters.</div>}

        {/* Screen preview mirrors physical Letter sheets: 1-up, 2-up, or 4-up. */}
        <div className="qr-screen-sheet-preview d-print-none">
          {chunk(currentCards, perSheet).map((sheet, index) => <div key={index} className={`qr-screen-sheet qr-screen-sheet-${printSize}`}>
            {sheet.map((card) => <div key={card.id} className="qr-screen-sheet-card">{renderCard(card)}</div>)}
          </div>)}
        </div>

        {/* Retain established physical DOM/CSS, including portrait rotation slots. */}
        <div className="qr-physical-print-render">
          {compact ? <div className="qr-print-sheet-list">
            {chunk(currentCards, 4).map((sheet, index) => <div key={index} className="qr-print-sheet qr-print-sheet-2x3">
              {sheet.map((card) => <div key={card.id} className={`qr-card-slot qr-card-slot-${printSize}`}>{renderCard(card)}</div>)}
            </div>)}
          </div> : <div className="qr-card-list">
            {currentCards.map((card) => printSize === "8x10-portrait"
              ? <div key={card.id} className="qr-card-slot qr-card-slot-8x10-portrait">{renderCard(card)}</div>
              : <React.Fragment key={card.id}>{renderCard(card)}</React.Fragment>)}
          </div>}
        </div>
        {!single && !printAll && totalPages > 1 && <div className="d-flex justify-content-between align-items-center mt-4 d-print-none">
          <button type="button" className="btn btn-outline-secondary" disabled={printing || currentPage === 1} onClick={() => goToPage(currentPage - 1)}>⬅ Prev</button>
          <div className="d-flex align-items-center gap-2">
            <label htmlFor="qr-print-page">Page</label>
            <select id="qr-print-page" className="form-select" style={{ width: "auto" }} disabled={printing} value={currentPage} onChange={(event) => goToPage(Number(event.target.value))}>
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((number) => <option key={number} value={number}>{number}</option>)}
            </select><span>of {totalPages}</span>
          </div>
          <button type="button" className="btn btn-outline-secondary" disabled={printing || currentPage === totalPages} onClick={() => goToPage(currentPage + 1)}>Next ➡</button>
        </div>}
      </div>
    </section>
  );
}
